// Rateio de honorários da vertical tributária — ESPELHO de
// backend/acordei_api/services/rateio.py (fonte única). Manter sincronizado;
// os dois lados têm teste pinando os mesmos percentuais.
//
// Doc canônico (CONTEXTO_ACORDEI_TRIBUTARIO.md, 2026-06-13): SEM assinatura;
// um único honorário de êxito REGRESSIVO por faixa, aplicado de forma DIRETA
// sobre o valor EFETIVAMENTE APROVEITADO; total = fee plataforma + honorário
// do contador; SEMPRE decompor (nunca só o total agregado).

export type FaixaRateio = {
  indice: number;
  rotulo: string;
  limiteInferior: number;          // exclusivo (R$); 0 na faixa 1
  limiteSuperior: number | null;   // inclusivo (R$); null = sem teto
  pctTotal: number;                // fee + honorário
  pctFee: number;                  // plataforma (ACORDEI!)
  pctHonorario: number;            // contador
};

export const FAIXAS_RATEIO: FaixaRateio[] = [
  { indice: 1, rotulo: "Até R$ 100 mil",           limiteInferior: 0,          limiteSuperior: 100_000,    pctTotal: 0.30, pctFee: 0.10, pctHonorario: 0.20 },
  { indice: 2, rotulo: "R$ 100 mil – R$ 1 milhão", limiteInferior: 100_000,    limiteSuperior: 1_000_000,  pctTotal: 0.25, pctFee: 0.10, pctHonorario: 0.15 },
  { indice: 3, rotulo: "R$ 1 mi – R$ 10 mi",       limiteInferior: 1_000_000,  limiteSuperior: 10_000_000, pctTotal: 0.20, pctFee: 0.10, pctHonorario: 0.10 },
  { indice: 4, rotulo: "Acima de R$ 10 milhões",   limiteInferior: 10_000_000, limiteSuperior: null,       pctTotal: 0.10, pctFee: 0.05, pctHonorario: 0.05 },
];

export function faixaParaValor(valorAproveitado: number): FaixaRateio {
  for (const f of FAIXAS_RATEIO) {
    if (f.limiteSuperior === null || valorAproveitado <= f.limiteSuperior) return f;
  }
  return FAIXAS_RATEIO[FAIXAS_RATEIO.length - 1];
}

export type DecomposicaoRateio = {
  creditoAproveitado: number;
  faixa: FaixaRateio;
  totalHonorarios: number;
  feePlataforma: number;
  honorarioContador: number;
  liquidoCliente: number;
};

const cent = (v: number) => Math.round(v * 100) / 100;

/** Decomposição canônica sobre o valor EFETIVAMENTE APROVEITADO. */
export function decomporHonorarios(valorAproveitado: number): DecomposicaoRateio {
  const v = Math.max(0, valorAproveitado || 0);
  const faixa = faixaParaValor(v);
  const totalHonorarios = cent(v * faixa.pctTotal);
  const feePlataforma = cent(v * faixa.pctFee);
  // honorário = resto, pra garantir fee + honorário == total ao centavo.
  const honorarioContador = cent(totalHonorarios - feePlataforma);
  const liquidoCliente = cent(v - totalHonorarios);
  return { creditoAproveitado: cent(v), faixa, totalHonorarios, feePlataforma, honorarioContador, liquidoCliente };
}

// ── Modelo CAPTADOR (Modelo A — espelho de rateio.py) ───────────────────────
// Clientes grandes/exclusivos trazidos por um captador. Remuneração FIXA (não
// regressiva): fee plataforma 10% + comissão do captador 5%.
export const CAPTADOR_PCT_FEE = 0.10;
export const CAPTADOR_PCT_CAPTADOR = 0.05;

export type DecomposicaoCaptador = {
  creditoAproveitado: number;
  pctTotal: number;
  pctFee: number;
  pctCaptador: number;
  totalHonorarios: number;
  feePlataforma: number;
  comissaoCaptador: number;
  liquidoCliente: number;
};

/** Decomposição do modelo CAPTADOR (fee fixo plataforma + comissão captador). */
export function decomporCaptador(valorAproveitado: number): DecomposicaoCaptador {
  const v = Math.max(0, valorAproveitado || 0);
  const pctTotal = CAPTADOR_PCT_FEE + CAPTADOR_PCT_CAPTADOR;
  const totalHonorarios = cent(v * pctTotal);
  const feePlataforma = cent(v * CAPTADOR_PCT_FEE);
  const comissaoCaptador = cent(totalHonorarios - feePlataforma);
  const liquidoCliente = cent(v - totalHonorarios);
  return {
    creditoAproveitado: cent(v), pctTotal, pctFee: CAPTADOR_PCT_FEE,
    pctCaptador: CAPTADOR_PCT_CAPTADOR, totalHonorarios, feePlataforma,
    comissaoCaptador, liquidoCliente,
  };
}

export const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
